import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Persona = "general" | "recruiter" | "developer" | "client";

interface PersonaContextType {
  persona: Persona;
  setPersona: (p: Persona) => void;
}

const PersonaContext = createContext<PersonaContextType>({
  persona: "general",
  setPersona: () => {},
});

function detectPersona(): Persona {
  const stored = sessionStorage.getItem("persona") as Persona | null;
  if (stored) return stored;

  // Check URL params
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref")?.toLowerCase();
  if (ref === "recruiter") return "recruiter";
  if (ref === "developer" || ref === "dev") return "developer";
  if (ref === "client" || ref === "business") return "client";

  // Check UTM source
  const utmSource = params.get("utm_source")?.toLowerCase() || "";
  if (utmSource.includes("linkedin") || utmSource.includes("indeed") || utmSource.includes("glassdoor")) return "recruiter";
  if (utmSource.includes("github") || utmSource.includes("stackoverflow") || utmSource.includes("hackernews")) return "developer";

  // Check referrer
  const referrer = document.referrer.toLowerCase();
  if (referrer.includes("linkedin.com") || referrer.includes("indeed.com")) return "recruiter";
  if (referrer.includes("github.com") || referrer.includes("stackoverflow.com") || referrer.includes("news.ycombinator.com")) return "developer";

  return "general";
}

export function PersonaProvider({ children }: { children: ReactNode }) {
  const [persona, setPersonaState] = useState<Persona>(detectPersona);

  const setPersona = (p: Persona) => {
    setPersonaState(p);
    sessionStorage.setItem("persona", p);
  };

  useEffect(() => {
    sessionStorage.setItem("persona", persona);
  }, [persona]);

  return (
    <PersonaContext.Provider value={{ persona, setPersona }}>
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona() {
  return useContext(PersonaContext);
}
